import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, ExternalLink } from 'lucide-react';
import api from '../services/api';

const GoogleOAuthTest = () => {
  const [authUrl, setAuthUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testResults, setTestResults] = useState([]);

  const addTestResult = (test, status, message) => {
    setTestResults(prev => [...prev, { test, status, message, timestamp: new Date() }]);
  };

  const runTests = async () => {
    setLoading(true);
    setError('');
    setTestResults([]);

    try {
      // Test 1: Check if backend is reachable
      addTestResult('Backend Health', 'running', 'Checking backend health...');
      const healthResponse = await api.get('/health');
      if (healthResponse.success) {
        addTestResult('Backend Health', 'passed', 'Backend is running and healthy');
      } else {
        addTestResult('Backend Health', 'failed', 'Backend health check failed');
        return;
      }

      // Test 2: Check Google OAuth URL endpoint
      addTestResult('Google OAuth URL', 'running', 'Fetching Google OAuth URL...');
      const urlResponse = await api.get('/auth/google/url');
      if (urlResponse.success && urlResponse.data.authUrl) {
        setAuthUrl(urlResponse.data.authUrl);
        addTestResult('Google OAuth URL', 'passed', 'Google OAuth URL generated successfully');
      } else {
        addTestResult('Google OAuth URL', 'failed', 'Failed to get Google OAuth URL');
        return;
      }

      // Test 3: Check environment variables
      addTestResult('Environment', 'running', 'Checking environment configuration...');
      const apiUrl = process.env.REACT_APP_API_URL;
      if (apiUrl) {
        addTestResult('Environment', 'passed', `API URL configured: ${apiUrl}`);
      } else {
        addTestResult('Environment', 'failed', 'REACT_APP_API_URL not configured');
      }

      addTestResult('All Tests', 'completed', 'All tests completed successfully');

    } catch (err) {
      console.error('Test error:', err);
      setError(err.message || 'Tests failed');
      addTestResult('Tests', 'failed', err.message || 'Tests failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (authUrl) {
      console.log('🔗 Redirecting to Google OAuth:', authUrl);
      window.location.href = authUrl;
    } else {
      setError('Google OAuth URL not available. Please run tests first.');
    }
  };

  useEffect(() => {
    runTests();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-24 pb-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Google OAuth Test
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Debug and test Google OAuth functionality
          </p>
        </motion.div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Test Results
            </h2>
            <button
              onClick={runTests}
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Running...' : 'Run Tests'}
            </button>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-700 dark:text-red-400">{error}</span>
            </div>
          )}

          <div className="space-y-2">
            {testResults.map((result, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border ${
                  result.status === 'passed'
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : result.status === 'failed'
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {result.test}
                  </span>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      result.status === 'passed'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : result.status === 'failed'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}
                  >
                    {result.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {result.message}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  {result.timestamp.toLocaleTimeString()}
                </p>
              </div>
            ))}
          </div>
        </div>

        {authUrl && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Google OAuth Test
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Google OAuth URL:
                </label>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-sm text-gray-600 dark:text-gray-400 break-all">
                  {authUrl}
                </div>
              </div>
              
              <button
                onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ExternalLink className="w-5 h-5" />
                <span>Test Google OAuth Login</span>
              </button>
              
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Click the button above to test the complete Google OAuth flow. 
                You should be redirected to Google, then back to the callback page.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleOAuthTest; 